import { useEffect } from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

import type { UWReviewVariant } from "@covertree/new-business-uw-decision";

import { useAppDispatch } from "@redux/hooks";
import {
  checkEndorsementUnderwriting,
  resetUnderwritingChecked,
  selectIsCheckingUnderwriting,
  selectEndorsementId,
  selectEndorsementPopulated,
  selectIsUnderwritingChecked,
  selectIsUnderwritingDeclined,
  selectIsUnderwritingFailed,
  selectShouldCheckUnderwriting,
  selectNeedsUnderwritingReview,
  selectUnderwritingNotes,
  useEndorsementSelector,
} from "@redux/slices/endorsement";
import { EndorsementRoutes, getEndorsementPath } from "@routing/paths";
import { useEndorsementStepGuard } from "@layouts/EndorsementLayout/hooks/useEndorsementStepGuard";

const getVariant = (
  isFailed: boolean,
  isDeclined: boolean
): UWReviewVariant => {
  if (isDeclined) return "declined";
  return isFailed ? "review" : "notes";
};

export const useUwReviewStep = () => {
  const { policyLocator = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { redirectTo } = useEndorsementStepGuard();
  const isPopulated = useEndorsementSelector(selectEndorsementPopulated);
  const shouldCheckUnderwriting = useEndorsementSelector(
    selectShouldCheckUnderwriting
  );
  const isUnderwritingChecked = useEndorsementSelector(
    selectIsUnderwritingChecked
  );
  const isCheckingUnderwriting = useEndorsementSelector(
    selectIsCheckingUnderwriting
  );
  const isUnderwritingFailed = useEndorsementSelector(
    selectIsUnderwritingFailed
  );
  const isUnderwritingDeclined = useEndorsementSelector(
    selectIsUnderwritingDeclined
  );
  const needsUnderwritingReview = useEndorsementSelector(
    selectNeedsUnderwritingReview
  );
  const notes = useEndorsementSelector(selectUnderwritingNotes);
  const endorsementId = useEndorsementSelector(selectEndorsementId);

  useEffect(() => {
    if (isPopulated && shouldCheckUnderwriting && !isUnderwritingChecked) {
      dispatch(checkEndorsementUnderwriting());
    }
  }, [dispatch, isPopulated, shouldCheckUnderwriting, isUnderwritingChecked]);

  useEffect(
    () => () => {
      dispatch(resetUnderwritingChecked());
    },
    [dispatch]
  );

  const isLoading =
    !isPopulated ||
    isCheckingUnderwriting ||
    (shouldCheckUnderwriting && !isUnderwritingChecked);

  const checkoutPath =
    !isLoading && !needsUnderwritingReview
      ? getEndorsementPath(policyLocator, EndorsementRoutes.CHECKOUT, {
          endorsementId: searchParams.get("endorsementId"),
        })
      : undefined;

  return {
    redirectTo: redirectTo ?? checkoutPath,
    isLoading,
    variant: getVariant(isUnderwritingFailed, isUnderwritingDeclined),
    notes,
    identifiers: [
      { label: "Endorsement ID", value: endorsementId ?? "" },
      { label: "Policy ID", value: policyLocator },
    ],
  };
};
